import { Typography } from '@material-ui/core';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import FusePageSimple from '@fuse/core/FusePageSimple';
import FuseAnimate from '@fuse/core/FuseAnimate/FuseAnimate';
import SnackbarOmni from 'allGlobalComponents/SnackbarOmni';
import TableOmniNew from 'allGlobalComponents/TableOmniNew';
import DateFilterWithExport from 'allGlobalComponents/DateFilterWithExport';
import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { formatDate } from 'app/utility/ExportToExcelUtility';
import { globalDateFormat } from 'app/constants';
import { useSnackbar, useApi, useExport, usePagination, useDateRange } from 'app/hooks';

const useStyles = makeStyles(theme => ({
  content: {
    width: '100%',
    padding: '0 16px',
    marginTop: '10px'
  },
  headerWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: theme.spacing(0, 2)
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center'
  },
  headerIcon: {
    marginRight: theme.spacing(1.5)
  },
  headerTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: theme.palette.text.primary
  }
}));

const STATE_OPTIONS = [
  { value: '', label: 'All States' },
  { value: 'connected', label: 'Connected' },
  { value: 'disconnected', label: 'Disconnected' },
  { value: 'break', label: 'Break' }
];

const formatDuration = (sec) => {
  if (!sec && sec !== 0) return '-';
  if (sec < 60) return `${sec}s`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  let result = '';
  if (h > 0) result += `${h}h `;
  if (m > 0) result += `${m}m `;
  if (s > 0) result += `${s}s`;
  return result.trim();
};

// Break reasons come from the API as raw keys (e.g. "lunch") — show them as "Lunch Break".
const formatReason = (value) => {
  if (!value) return '-';
  const words = String(value).replace(/[_-]+/g, ' ').trim();
  if (!words) return '-';
  const capitalized = words.charAt(0).toUpperCase() + words.slice(1);
  return /break$/i.test(capitalized) ? capitalized : `${capitalized} Break`;
};

const statePillMapping = (value) => {
  const map = {
    connected: { label: 'Connected', color: '#166534', bgColor: '#dcfce7' },
    disconnected: { label: 'Disconnected', color: '#991b1b', bgColor: '#fee2e2' },
    break: { label: 'Break', color: '#854d0e', bgColor: '#fef9c3' }
  };
  const key = value?.toLowerCase();
  return map[key] || { label: value ? value.charAt(0).toUpperCase() + value.slice(1) : '-' };
};

function CallCenterAgentLogs() {
  const classes = useStyles();
  const theme = useTheme();

  // User data
  const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
  const userId = userData.id;

  // Custom Hooks
  const { snackbar, showError, showWarning } = useSnackbar();
  const { isLoading, request } = useApi();
  const { isExporting, handleExport } = useExport();
  const { currentParams, setCurrentParams, totalItems, setTotalItems, resetPage } = usePagination(20);
  const { startDate, endDate, handleDateChange: onDateRangeChange } = useDateRange(true);

  // Local State
  const [data, setData] = useState([]);
  const [filterValues, setFilterValues] = useState({
    state: '',
    extension: ''
  });

  const buildPayload = useCallback(() => {
    const payload = {
      page: currentParams.page,
      limit: currentParams.limit,
      start_date: startDate,
      end_date: endDate,
      userId: userId
    };
    if (filterValues.state) payload.state = filterValues.state;
    if (filterValues.extension?.trim()) payload.extension = filterValues.extension.trim();
    return payload;
  }, [currentParams, filterValues, startDate, endDate, userId]);

  useEffect(() => {
    request({
      module: 'pbx',
      method: 'pbxAgentLogsGet',
      payload: buildPayload(),
      onSuccess: (response) => {
        const res = response.data.data;
        setData(res.logs || []);
        setTotalItems(res.totalItems || 0);
      },
      onError: (message) => showError(message || 'Error fetching agent logs')
    });
  }, [currentParams, filterValues, startDate, endDate]);

  const handleDateChange = (range) => {
    onDateRangeChange(range);
    resetPage();
  };

  const handleFiltersApply = (filters) => {
    setFilterValues(filters);
    resetPage();
  };

  const handleFiltersClear = (filters) => {
    setFilterValues(filters);
    resetPage();
  };

  const handleRefresh = () => {
    setCurrentParams(prev => ({ ...prev }));
  };

  const exportColumns = [
    { header: 'S.No', key: '_index' },
    { header: 'ID', key: 'id' },
    { header: 'Agent', key: 'agent_username', transform: (v) => v || '-' },
    { header: 'Extension', key: 'account_extension', transform: (v) => v || '-' },
    { header: 'State', key: 'state', transform: (v) => v ? v.charAt(0).toUpperCase() + v.slice(1) : '-' },
    { header: 'Previous State', key: 'previous_state', transform: (v) => v ? v.charAt(0).toUpperCase() + v.slice(1) : '-' },
    { header: 'Reason', key: 'reason', transform: (v) => formatReason(v) },
    { header: 'Duration', key: 'duration_sec', transform: (v) => formatDuration(v) },
    { header: 'Started At', key: 'started_at', transform: (v) => formatDate(v) },
    { header: 'Ended At', key: 'ended_at', transform: (v) => formatDate(v) },
    { header: 'Created', key: 'dt', transform: (v) => formatDate(v) }
  ];

  const onExport = () => {
    const exportPayload = { ...buildPayload(), page: 0, limit: totalItems || 10000 };

    handleExport({
      module: 'pbx',
      method: 'pbxAgentLogsGet',
      payload: exportPayload,
      fileNamePrefix: 'agent_logs_report',
      sheetName: 'Agent Logs',
      getDataFromResponse: (res) => res.data.data.logs || [],
      columns: exportColumns,
      onError: (msg) => showWarning(msg || 'No data to export')
    });
  };

  const filterFields = [
    {
      name: 'extension',
      label: 'Extension',
      type: 'text',
      placeholder: 'e.g. 1101',
      defaultValue: ''
    },
    {
      name: 'state',
      label: 'State',
      type: 'select',
      options: STATE_OPTIONS,
      defaultValue: ''
    }
  ];

  const headCells = [
    { id: 'id', numeric: false, disablePadding: false, label: 'ID' },
    {
      id: 'agent_username',
      numeric: false,
      disablePadding: false,
      label: 'Agent',
      tooltip: true,
      customRow: row => row.agent_username || '-'
    },
    {
      id: 'account_extension',
      numeric: false,
      disablePadding: false,
      label: 'Extension',
      tooltip: true,
      customRow: row => row.account_extension || '-'
    },
    {
      id: 'state',
      numeric: false,
      disablePadding: false,
      label: 'State',
      pill: true,
      pillConfig: { mapping: statePillMapping }
    },
    {
      id: 'previous_state',
      numeric: false,
      disablePadding: false,
      label: 'Previous State',
      pill: true,
      pillConfig: { mapping: statePillMapping }
    },
    {
      id: 'reason',
      numeric: false,
      disablePadding: false,
      label: 'Reason',
      customRow: row => formatReason(row.reason)
    },
    {
      id: 'duration_sec',
      numeric: true,
      disablePadding: false,
      label: 'Duration',
      customRow: row => formatDuration(row.duration_sec)
    },
    { id: 'started_at', numeric: false, disablePadding: false, label: 'Started', dt: true },
    {
      id: 'ended_at',
      numeric: false,
      disablePadding: false,
      label: 'Ended',
      customRow: row => row.ended_at ? moment(row.ended_at).format(globalDateFormat) : 'Active'
    },
    { id: 'dt', numeric: false, disablePadding: false, label: 'Created', dt: true }
  ];

  return (
    <FusePageSimple
      header={
        <div className={classes.headerWrapper}>
          <div className={classes.headerLeft}>
            <FuseAnimate animation="transition.expandIn" delay={300}>
              <HeadsetMicIcon fontSize="large" className={classes.headerIcon} />
            </FuseAnimate>
            <FuseAnimate animation="transition.slideLeftIn" delay={300}>
              <Typography variant="h6" className={classes.headerTitle}>
                Agent Logs
              </Typography>
            </FuseAnimate>
          </div>
        </div>
      }
      content={
        <div className={classes.content}>
          <DateFilterWithExport
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            showDateRange={true}
            showExport={true}
            onExport={onExport}
            isExporting={isExporting}
            filterFields={filterFields}
            filterValues={filterValues}
            onFiltersApply={handleFiltersApply}
            onFiltersClear={handleFiltersClear}
            showRefresh={true}
            onRefresh={handleRefresh}
          />

          <TableOmniNew
            rows={data}
            headCells={headCells}
            perPageRows={currentParams.limit}
            totalItems={totalItems}
            curPage={currentParams.page}
            setCurrentParams={setCurrentParams}
            loading={isLoading}
            isLoadingPagination={false}
            fixedHeight={true}
            maxTableHeight="600px"
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />

          <SnackbarOmni {...snackbar} />
        </div>
      }
    />
  );
}

export default React.memo(CallCenterAgentLogs);